import { useCallback, useEffect, useRef, useState } from 'react';
import { useIonRouter, useIonViewWillEnter } from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import { useHistory, useLocation } from 'react-router-dom';
import { apiCall } from '@/util/api';
import { useUserContext } from '@/modules/user/useUserContext';
import { TopicLogic } from './logic';
import type { Topic, Registration } from './types';
import type { TopicViewMode } from './TopicView';
import { hasOtherTabs, cleanupTabId } from '@/services/crossTabCommunication';
import { useTopicState } from './TopicStateContext';
import {
  startShareSession,
  type ShareUpdatesStart,
} from '@/modules/share/session_storage';
interface UseTopicControllerParams {
  initialMode: TopicViewMode;
  topicId?: string;
  resetCreateForm: () => void;
  resetFormFromTopic: (topic: Topic) => void;
}

interface UseTopicControllerState {
  mode: TopicViewMode;
  topic: Topic | null;
  registration: Registration | null;
  loading: boolean;
  deleting: boolean;
  showDeleteSheet: boolean;
  setShowDeleteSheet: (value: boolean) => void;
  fetchTopicInfo: () => Promise<void>;
  handleEnterUpdate: () => void;
  handleCancelUpdate: () => void;
  handleFinishUpdate: (updatedTopic: Topic) => void;
  handleShareTopic: () => void;
  handleShareUpdates: (start: ShareUpdatesStart) => void;
  handleDeleteTopic: () => Promise<void>;
}

export function useTopicController({
  initialMode,
  topicId,
  resetCreateForm,
  resetFormFromTopic,
}: UseTopicControllerParams): UseTopicControllerState {
  const history = useHistory();
  const router = useIonRouter();
  const location = useLocation();
  const userContext = useUserContext();
  const [present] = useAppToast();
  const { state: topicState, dispatch: dispatchTopicState } = useTopicState();

  const [mode, setMode] = useState<TopicViewMode>(initialMode);
  // Read the topic straight from the shared TopicStateContext. The list view
  // and detail view share this map, so an edit dispatched anywhere is
  // immediately visible everywhere — no separate local cache to keep in sync.
  const topic: Topic | null = (topicId && topicState.byId[topicId]) || null;
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [loading, setLoading] = useState(initialMode !== 'create');
  const [showDeleteSheet, setShowDeleteSheet] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fetchGenerationRef = useRef(0);
  const activeFetchControllerRef = useRef<AbortController | null>(null);
  const prevUserContextRef = useRef<{
    userId?: string;
    claims: string;
  } | null>(null);

  const invalidateTopicFetches = useCallback(() => {
    fetchGenerationRef.current += 1;
    activeFetchControllerRef.current?.abort();
    activeFetchControllerRef.current = null;
  }, []);

  const fetchTopicInfo = useCallback(async () => {
    if (!topicId) return;

    activeFetchControllerRef.current?.abort();
    const fetchGeneration = ++fetchGenerationRef.current;
    const controller = new AbortController();
    activeFetchControllerRef.current = controller;
    setLoading(true);

    try {
      const encodedTopicId = encodeURIComponent(topicId);
      const response = await apiCall(`/api/topics/${encodedTopicId}`, {
        signal: controller.signal,
      });

      if (
        controller.signal.aborted ||
        fetchGeneration !== fetchGenerationRef.current
      ) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const nextTopic = TopicLogic.fromJSON(data);
        dispatchTopicState({ type: 'UPSERT_TOPIC', topic: nextTopic });
        resetFormFromTopic(nextTopic);
      } else if (response.status === 404) {
        // A 404 may be a genuinely missing topic OR a by-invitation-only topic
        // the caller can't read yet. TopicView decides what to render (the
        // not-found card, or a private-invite placeholder), so we don't toast
        // here — a toast would contradict the private-invite placeholder.
        dispatchTopicState({ type: 'REMOVE_TOPIC', topicId });
      } else {
        await present({
          message: 'Failed to load topic',
          duration: 3000,
          color: 'danger',
        });
      }
    } catch {
      if (controller.signal.aborted) {
        return;
      }
      await present({
        message: 'Network error occurred',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      if (
        !controller.signal.aborted &&
        fetchGeneration === fetchGenerationRef.current
      ) {
        setLoading(false);
      }
    }
  }, [topicId, dispatchTopicState, present, resetFormFromTopic]);

  const checkRegistration = useCallback(async () => {
    if (!topicId) return;

    try {
      const encodedTopicId = encodeURIComponent(topicId);
      const response = await apiCall(
        `/api/topic-registrations?topic_id=${encodedTopicId}`
      );

      if (response.ok) {
        const data: Registration | null = await response.json();
        setRegistration(data);
      } else {
        setRegistration(null);
      }
    } catch {
      setRegistration(null);
    }
  }, [topicId]);

  useEffect(() => {
    void hasOtherTabs();
  }, []);

  useEffect(() => {
    return () => {
      invalidateTopicFetches();
      void cleanupTabId();
    };
  }, [invalidateTopicFetches]);

  useEffect(() => {
    if (initialMode === 'create') {
      resetCreateForm();
    }
  }, [initialMode, resetCreateForm]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useIonViewWillEnter(() => {
    if (initialMode !== 'create' && topicId) {
      fetchTopicInfo();
      if (initialMode === 'view') {
        checkRegistration();
      }
    }
  });

  useEffect(() => {
    if (initialMode !== 'view') {
      return;
    }

    const currentUserContext = {
      userId: userContext.userId,
      claims: JSON.stringify(userContext.claims),
    };

    if (prevUserContextRef.current === null) {
      prevUserContextRef.current = currentUserContext;
      return;
    }

    const prev = prevUserContextRef.current;
    if (
      prev.userId !== currentUserContext.userId ||
      prev.claims !== currentUserContext.claims
    ) {
      if (topicId) {
        // Auth/verification changed (e.g. the user re-verified a phone number and
        // is now an invited user). Re-fetch the topic so visibility changes show
        // immediately: an topic that just became visible loads, and one that is
        // no longer visible 404s and is removed from view.
        fetchTopicInfo();
        checkRegistration();
      }
    }

    prevUserContextRef.current = currentUserContext;
  }, [fetchTopicInfo, checkRegistration, topicId, initialMode, userContext]);

  const handleEnterUpdate = useCallback(() => {
    if (!topicId) return;
    history.push(`/topics/${encodeURIComponent(topicId)}/edit`);
  }, [topicId, history]);

  const handleCancelUpdate = useCallback(() => {
    invalidateTopicFetches();
    if (topic) {
      resetFormFromTopic(topic);
    }
    setLoading(false);
    if (!topicId) return;
    history.replace(`/topics/${encodeURIComponent(topicId)}`);
  }, [topic, topicId, history, invalidateTopicFetches, resetFormFromTopic]);

  const handleFinishUpdate = useCallback(
    (updatedTopic: Topic) => {
      invalidateTopicFetches();
      dispatchTopicState({ type: 'UPSERT_TOPIC', topic: updatedTopic });
      resetFormFromTopic(updatedTopic);
      setLoading(false);
      if (!topicId) return;
      history.replace(`/topics/${encodeURIComponent(topicId)}`);
    },
    [
      topicId,
      dispatchTopicState,
      history,
      invalidateTopicFetches,
      resetFormFromTopic,
    ]
  );

  const navigateToShare = useCallback(async () => {
    if (!topic || !topicId) return;
    // Passes on the leg the visitor arrived on (`?legId=`), the same way the
    // welcome sheet's button of the same name does, so the legs this share
    // sends join that leg's run.
    //
    // With no leg in the URL it is a plain share, which drops a recipient
    // selection abandoned from an earlier share of this topic (#479 persists it
    // so the set-names detour and mid-flow reloads restore it). An in-progress
    // send is left intact, since tapping Share (rather than the return link) is
    // a valid way back to it.
    const previousLegId =
      new URLSearchParams(location.search).get('legId') ?? undefined;
    await startShareSession(topicId, {
      reshare: previousLegId ? { previousLegId } : undefined,
      typeDefaultsToExcludingAware:
        TopicLogic.defaultsToExcludingAwareRecipients(topic.type),
    });
    router.push(
      `/topics/${encodeURIComponent(topicId)}/share`,
      'forward',
      'push',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { from: location.pathname } as any
    );
  }, [topic, topicId, location.pathname, location.search, router]);

  const handleShareTopic = useCallback(() => {
    if (!topic || !topicId) return;
    void navigateToShare();
  }, [topic, topicId, navigateToShare]);

  // Opens the share wizard with a change list (#427). Rather than pass the
  // update head/summary as URL params, bootstrap a persisted wizard session with
  // them first — persisted state is the single carrier, and the wizard restores
  // it on mount. A reshare also passes on the update leg it arrived on, with
  // the received summary; a sender sharing their own changes writes one in the
  // Changes step.
  const handleShareUpdates = useCallback(
    async (start: ShareUpdatesStart) => {
      if (!topic || !topicId) return;
      await startShareSession(topicId, start);
      router.push(
        `/topics/${encodeURIComponent(topicId)}/share`,
        'forward',
        'push',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { from: location.pathname } as any
      );
    },
    [topic, topicId, location.pathname, router]
  );

  const handleDeleteTopic = useCallback(async () => {
    if (!topic || !topicId) return;

    setDeleting(true);
    try {
      const encodedTopicId = encodeURIComponent(topicId);
      const response = await apiCall(`/api/topics/${encodedTopicId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await present({
          message: 'Topic deleted successfully',
          duration: 3000,
          color: 'success',
        });
        // Defer the removal until just before the redirect so the deleted
        // topic stays visible underneath the success toast (otherwise the
        // view would flash "Topic Not Found" because `topic` is read from
        // the context's byId map).
        setTimeout(() => {
          dispatchTopicState({ type: 'REMOVE_TOPIC', topicId });
          window.location.href = '/topics';
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        await present({
          message:
            data.message ||
            'Failed to delete topic. You may not have permission.',
          duration: 3000,
          color: 'danger',
        });
      }
    } catch {
      await present({
        message: 'Network error occurred',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setDeleting(false);
      setShowDeleteSheet(false);
    }
  }, [topic, topicId, dispatchTopicState, present]);

  return {
    mode,
    topic,
    registration,
    loading,
    deleting,
    showDeleteSheet,
    setShowDeleteSheet,
    fetchTopicInfo,
    handleEnterUpdate,
    handleCancelUpdate,
    handleFinishUpdate,
    handleShareTopic,
    handleShareUpdates,
    handleDeleteTopic,
  };
}
